/**
 * Centralized API layer for the Minerva Flask backend.
 *
 * Why this file exists: every fetch() call used to live inline inside
 * FilterBar.jsx, mixed in with UI state logic. That made a real bug
 * (wrong .NET routes, wrong casing) expensive to fix - the URLs were
 * scattered across the component instead of living in one place.
 *
 * From now on, FilterBar.jsx (and any future component that needs this
 * data) calls the functions below instead of fetch() directly. If the
 * Flask route convention changes, this is the only file that needs
 * editing.
 */

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

async function getJSON(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new Error(`Request to ${path} failed: ${res.status}`);
  }
  return res.json();
}

export function getVerticalStacks() {
  return getJSON("/vertical-stacks");
}

export function getSubStacks(verticalStackId) {
  const query = verticalStackId != null ? `?vertical_stack_id=${verticalStackId}` : "";
  return getJSON(`/sub-stacks${query}`);
}

export function getJobTitles(subStackId) {
  const query = subStackId != null ? `?sub_stack_id=${subStackId}` : "";
  return getJSON(`/job-titles${query}`);
}

export function getTimePeriods() {
  return getJSON("/time-periods");
}

export function getTerritories() {
  return getJSON("/territories");
}

export function getCompensationPivot({
  verticalStackId,
  // FIXED 2026-08-04: was 2 (base salary only). The real Angular app's
  // actual runtime default is 1 (fully-loaded cost) - see
  // core-compensation.component.ts's `model = { costingTypeId: 1, ... }`,
  // which is what's really sent to GetCompensationPivot every time the old
  // app loads. The old "2" here was copied from GetCompensationPivot.sql's
  // own top-of-file `declare @CostingTypeId INT = 2`, which is just an
  // arbitrary scratch value the original author used when testing the
  // query directly in SSMS - not what the app itself sends. With 2, the
  // pivot's `amount` resolved to base_l2 (base salary component) instead
  // of full_l2 (fully-loaded cost) - the same field Cost Band Detail
  // already correctly uses (costing_type_id=1 there), which is exactly why
  // Cost Band Detail matched old Minerva and the Role List table didn't.
  costingTypeId = 1,
  timePeriodId,
  durationId = 1,
  jobTitleId = null,
  isCore = true,
  isAll = false,
}) {
  const params = new URLSearchParams({
    vertical_stack_id: verticalStackId ?? 0,
    costing_type_id: costingTypeId,
    duration_id: durationId,
    is_core: isCore,
    is_all: isAll,
  });
  if (timePeriodId != null) params.set("time_period_id", timePeriodId);
  if (jobTitleId != null) params.set("job_title_id", jobTitleId);
  return getJSON(`/compensation/pivot?${params}`);
}

// ---------------------------------------------------------------------------
// Embedded Job Specification panel (comp page) - see routes.py for the
// matching Flask routes and provenance notes.
// ---------------------------------------------------------------------------

/** Single-role JD - powers JobSpecPanel.jsx's top card. */
export function getJobDescription(jobTitleId) {
  return getJSON(`/job-description?job_title_id=${jobTitleId}`);
}

/** Change-history timeline for one JD row (by job_description.id, not job_title_id). */
export function getJobDescriptionLogs(jobDescriptionId) {
  return getJSON(`/job-description-logs?job_description_id=${jobDescriptionId}`);
}

/** "Skill Set Matrix" info card. */
export function getSkillSetMatrix(jobTitleId) {
  return getJSON(`/skill-set-matrix?job_title_id=${jobTitleId}`);
}

/** "Rating" table rows. */
export function getSkillSetMatrixNonTech(jobTitleId) {
  return getJSON(`/skill-set-matrix-non-tech?job_title_id=${jobTitleId}`);
}

// ---------------------------------------------------------------------------
// JD Skills page - Job Descriptions Comparison tool.
// ---------------------------------------------------------------------------

/** 3-role ladder (role below / selected / role above) for one job title. */
export function getJobDescriptionCompareRoles(jobTitleId) {
  return getJSON(`/job-description-compare-roles?job_title_id=${jobTitleId}`);
}

/**
 * Flat rows for Skills Matrix Comparison, filtered server-side by vertical
 * stack only. Sub Stack / Job Title filtering happens client-side in
 * SkillsMatrixComparison.jsx - matching the real Angular source's own
 * client-side filtering, not a gap introduced here.
 */
export function getSkillMatrixAiData(verticalStackIds) {
  const ids = (verticalStackIds || []).join(",");
  return getJSON(`/skill-matrix-ai-data?vertical_stack_ids=${encodeURIComponent(ids)}`);
}

// ---------------------------------------------------------------------------
// Trends page - Job Wages & Inflation Comparison (see backend/routes.py
// /api/salary-comparison/*). One call per tab plus the Salary Breakdown
// dialog. The old Angular SalaryComparisionService sent camelCase params;
// these use the Flask snake_case convention - same values, same endpoints'
// logic underneath.
// ---------------------------------------------------------------------------

/** Single Role tab: one role x one territory over the year window. */
export function getSalaryComparisonSingleRole({ startYear, endYear, startMonth, territoryId, minervaJobCode }) {
  const params = new URLSearchParams({
    start_year: startYear,
    end_year: endYear,
    start_month: startMonth,
    territory_id: territoryId,
    minerva_job_code: minervaJobCode,
  });
  return getJSON(`/salary-comparison/single-role?${params}`);
}

/** Two Roles tab: two roles side by side in one territory. */
export function getSalaryComparisonTwoRoles({ startYear, endYear, startMonth, territoryId, minervaJobCodeA, minervaJobCodeB }) {
  const params = new URLSearchParams({
    start_year: startYear,
    end_year: endYear,
    start_month: startMonth,
    territory_id: territoryId,
    minerva_job_code_a: minervaJobCodeA,
    minerva_job_code_b: minervaJobCodeB,
  });
  return getJSON(`/salary-comparison/two-roles?${params}`);
}

/** Role x 2 Locations tab: one role across two territories + cost gap. */
export function getSalaryComparisonTwoTerritories({ startYear, endYear, startMonth, territoryIdA, territoryIdB, minervaJobCode }) {
  const params = new URLSearchParams({
    start_year: startYear,
    end_year: endYear,
    start_month: startMonth,
    territory_id_a: territoryIdA,
    territory_id_b: territoryIdB,
    minerva_job_code: minervaJobCode,
  });
  return getJSON(`/salary-comparison/two-territories?${params}`);
}

/**
 * Salary Breakdown dialog. NOTE: startMonth/endMonth here are month NAMES
 * ('Jan'..'Dec'), derived from the clicked row's review_period label -
 * matching the old GetAvgSalaryDataForTimePeriod contract, which differs
 * from the numeric months the three calls above take.
 */
export function getSalaryBreakdown({ minervaJobCode, territoryId, startMonth, startYear, endMonth, endYear }) {
  const params = new URLSearchParams({
    minerva_job_code: minervaJobCode,
    territory_id: territoryId,
    start_month: startMonth,
    start_year: startYear,
    end_month: endMonth,
    end_year: endYear,
  });
  return getJSON(`/salary-comparison/breakdown?${params}`);
}

// ---------------------------------------------------------------------------
// Compare page - Blended Roles (see backend/routes.py). The matcher POST is
// the old .NET POST /api/JobSpecComparison?verticalStackId=N, now served by
// Flask + Gemini with the same stabilization pass.
// ---------------------------------------------------------------------------

/**
 * Match a pasted/uploaded JD against the vertical's job descriptions.
 * payload = { ...excelTemplateFields, userInput } (camelCase keys).
 */
export async function postJobSpecComparison(payload, verticalStackId) {
  const res = await fetch(`${API_BASE}/job-spec-comparison?vertical_stack_id=${verticalStackId || 0}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`Job spec comparison failed: ${res.status}`);
  }
  return res.json();
}

/** Latest-period compensation rows for 1-3 selected roles (comma ids). */
export function getCompensationForBlended(jobTitleIds) {
  return getJSON(`/compensation-for-blended?job_title_ids=${encodeURIComponent(jobTitleIds)}`);
}

/** Latest time period with data - for the cost breakdown popup payload. */
export function getLatestTimePeriod() {
  return getJSON("/latest-time-period");
}

// ---------------------------------------------------------------------------
// Job Matching Agent - see backend/routes.py (/api/agent/*), a thin proxy to
// the IRIS chat engine. The message call itself is a streaming fetch made
// directly in AgentPage.jsx (SSE), not via getJSON.
// ---------------------------------------------------------------------------

/** Liveness of the IRIS service, so the Agent page can show an offline badge. */
export function agentHealth() {
  return getJSON("/agent/health");
}
